import { parseFlowJson } from 'utils/metaFlowJson';
import { validateFlowStructure } from 'utils/metaFlowValidation';

export const META_FLOW_SCREENS_FORMAT = 'waba_meta_flow_screens';
export const META_FLOW_SCREENS_VERSION = 1;

/**
 * Builder-native export: screens + field values (labels, options, API keys, etc.).
 */
export const buildScreensExportPayload = ({
  screens = [],
  name = '',
  description = '',
  isDynamic = false,
  categories = ['OTHER'],
} = {}) => ({
  format: META_FLOW_SCREENS_FORMAT,
  version: META_FLOW_SCREENS_VERSION,
  exported_at: new Date().toISOString(),
  name: name || '',
  description: description || '',
  is_dynamic: Boolean(isDynamic),
  categories: Array.isArray(categories) && categories.length ? categories : ['OTHER'],
  screens,
});

export const downloadScreensExport = (payload, filename) => {
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `metaflow-screens-${Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const normalizeImportedScreens = (screens) => {
  if (!Array.isArray(screens) || screens.length === 0) {
    throw new Error('Import JSON must include at least one screen');
  }

  return screens.map((screen, index) => {
    if (!screen || typeof screen !== 'object') {
      throw new Error(`Screen ${index + 1} is invalid`);
    }

    const id =
      String(screen.id || `screen_${index + 1}`)
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/^_+|_+$/g, '') || `screen_${index + 1}`;

    return {
      ...screen,
      id,
      title: screen.title || `Screen ${index + 1}`,
      components: Array.isArray(screen.components) ? screen.components : [],
      tenant_api_uri: screen.tenant_api_uri || '',
    };
  });
};

/**
 * Accepts:
 * - Waba builder export ({ format, screens, is_dynamic, ... })
 * - { screens: [...] }
 * - Meta Flow JSON ({ version, screens, routing_model })
 */
export const parseScreensImport = (rawInput) => {
  let parsed = rawInput;
  if (typeof rawInput === 'string') {
    const trimmed = rawInput.trim();
    if (!trimmed) {
      throw new Error('Paste JSON to import');
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      throw new Error('Invalid JSON — check the pasted content');
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Import JSON must be an object');
  }

  // Builder export or { screens: [...] }
  if (Array.isArray(parsed.screens) && parsed.screens[0]?.components !== undefined) {
    const screens = normalizeImportedScreens(parsed.screens);
    const structuralErrors = validateFlowStructure(screens, {
      isDynamic: Boolean(parsed.is_dynamic),
    });
    // Allow import even with endpoint warnings; only block hard structure issues.
    const blocking = structuralErrors.filter(
      (err) => !err.includes('no tenant API endpoint')
    );
    if (blocking.length > 0) {
      throw new Error(blocking[0]);
    }

    return {
      screens,
      name: typeof parsed.name === 'string' ? parsed.name : undefined,
      description: typeof parsed.description === 'string' ? parsed.description : undefined,
      isDynamic:
        typeof parsed.is_dynamic === 'boolean' ? parsed.is_dynamic : undefined,
      categories: Array.isArray(parsed.categories) ? parsed.categories : undefined,
    };
  }

  // Meta Flow JSON (no builder `components` — uses layout.children)
  if (Array.isArray(parsed.screens) && parsed.version) {
    const screens = normalizeImportedScreens(parseFlowJson(parsed));
    if (!screens.length) {
      throw new Error('Could not parse Meta Flow JSON screens');
    }
    return {
      screens,
      isDynamic: Boolean(parsed.data_api_version),
    };
  }

  // Bare screens array
  if (Array.isArray(parsed)) {
    const screens = normalizeImportedScreens(parsed);
    return { screens };
  }

  throw new Error(
    'Unrecognized format. Export from this builder, or paste { "screens": [...] } / Meta Flow JSON.'
  );
};
